// The optimistic-click overlay: turning the click ledger into instance data.
//
// SPEC §3 requires three failure outcomes to LOOK DIFFERENT — an illegal click
// flashes and returns the click to the bank, a lost click snaps back and reads as
// "someone beat you to it", a dropped click is refunded and says so. This module
// is where that requirement becomes bytes: an outcome maps to a slot, a slot maps
// to a colour and a shape in the fragment shader, and the three never collapse
// into one flash.
//
// Packing is a pure function so the mapping can be tested without a GL context.
// It is the part that is easy to get subtly wrong — an off-by-one in the slot
// table makes "lost" render as "illegal", which is exactly the confusion §3 is
// trying to prevent, and it would look fine.

import {
  OutcomeApplied,
  OutcomeCaptured,
  OutcomeDropped,
  OutcomeIllegal,
  OutcomeLost,
  OutcomePending,
  type Outcome,
  type OptimisticClick,
} from '../net/clicks';
import { BoardW } from '../wire/core';
import tokens from '../tokens.json';

/** The shader's `uColours` index. Order is load-bearing: it is the array layout. */
export const SlotPending = 0;
export const SlotApplied = 1;
export const SlotCaptured = 2;
export const SlotIllegal = 3;
export const SlotLost = 4;
export const SlotDropped = 5;
export const SlotCount = 6;

/** Outcomes that are not drawn at all. `settled` has nothing to say. */
export const SlotNone = -1;

export function outcomeSlot(o: Outcome): number {
  switch (o) {
    case OutcomePending:
      return SlotPending;
    case OutcomeApplied:
      return SlotApplied;
    case OutcomeCaptured:
      return SlotCaptured;
    case OutcomeIllegal:
      return SlotIllegal;
    case OutcomeLost:
      return SlotLost;
    case OutcomeDropped:
      return SlotDropped;
    default:
      return SlotNone;
  }
}

/**
 * How long each outcome is on screen, in milliseconds.
 *
 * These are not arbitrary. §15's tracer runs ~600 ms and its arc fades over
 * ~1.2 s, and a capture "holds ~200 ms longer" than an ordinary hit. The refusal
 * cases are shorter because they are information, not spectacle — and `lost` is
 * the longest of the three because it is the one a player will want to have seen
 * after the fact.
 */
export const HOLD_MS: Readonly<Record<number, number>> = {
  [SlotPending]: 1200,
  [SlotApplied]: 500,
  [SlotCaptured]: 900,
  [SlotIllegal]: 600,
  [SlotLost]: 800,
  [SlotDropped]: 700,
};

/**
 * The palette, from tokens.json rather than from constants here — §15 makes the
 * token file the single source of truth precisely so the interface and the
 * server-rendered share card cannot drift apart.
 *
 *   pending   cyan       the interface's own colour: this is your action, unjudged
 *   applied   phosphor   a data readout, quiet
 *   captured  phosphor   bright, and the shader blooms it
 *   illegal   amber      warnings and countdowns
 *   lost      alert red  reserved strictly for alerts, and losing a race is one
 *   dropped   textDim    nothing happened; do not dress it as an event
 */
export const OVERLAY_COLOURS: readonly string[] = [
  tokens.color.cyan,
  tokens.color.phosphor,
  tokens.color.phosphor,
  tokens.color.amber,
  tokens.color.alert,
  tokens.color.textDim,
];

/** Four floats per instance: board x, board y, slot, age. */
export const OVERLAY_STRIDE_FLOATS = 4;

export interface PackedOverlay {
  data: Float32Array<ArrayBuffer>;
  count: number;
}

/**
 * Packs the drawable clicks into instance attributes.
 *
 * `age` runs 0..1 over the outcome's hold window and is what every animation in
 * the overlay shader is driven by, so a slow frame stretches the animation rather
 * than skipping it.
 */
export function packOverlay(
  clicks: readonly OptimisticClick[],
  now: number,
  into?: Float32Array<ArrayBuffer>,
): PackedOverlay {
  const need = clicks.length * OVERLAY_STRIDE_FLOATS;
  const data = into && into.length >= need ? into : new Float32Array(Math.max(need, 64));
  let n = 0;
  for (const c of clicks) {
    const slot = outcomeSlot(c.outcome);
    if (slot === SlotNone) continue;
    const start = slot === SlotPending ? c.bornAt : c.resolvedAt;
    const hold = HOLD_MS[slot] ?? 600;
    let age = (now - start) / hold;
    if (age < 0) age = 0;
    if (age > 1) {
      // A pending click that has outlived its window keeps pulsing rather than
      // vanishing: the click IS still outstanding, and a marker that disappears
      // while the server has not answered says the opposite of the truth.
      if (slot !== SlotPending) continue;
      age = age % 1;
    }
    const at = n * OVERLAY_STRIDE_FLOATS;
    data[at] = c.target % BoardW;
    data[at + 1] = (c.target / BoardW) | 0;
    data[at + 2] = slot;
    data[at + 3] = age;
    n++;
  }
  return { data, count: n };
}
